/*
PW ComboBox

A text field that additionally presents a dropdown list of options to choose from.

All props allowed by the TextInput component are also allowed here, and its behavior
with regard to save/cancel is generally identical to that of TextInput. It also allows
any of the props recognized by the MenuOwner mixin for menu anchoring etc.

In addition, it allows the following props:
  - options: an array of values to be presented in the dropdown; these can be either
    strings, or objects with value/display properties.
  - saveOnSelect: whether the TextInput should invoke onSave when the user chooses a
    dropdown value (defaults to true)
  - menuClasses: optional extra classNames to be added to the menu element

TODO:
  - Support filtering the list by the field value
  - Disabled state
  - Keyboard selection from the list
  - Refine control over opening/closing the list

*/

import _ from 'lodash'
import React from 'react'
import T from 'prop-types'
import cx from 'classnames'
import MenuOwner from './MenuOwner'
import TextInput from './TextInput'
import MenuItem from './MenuItem'
import ScrollBars from './scrollbars/ScrollBars'

export default class ComboBox extends React.Component {
  static displayName = 'ComboBox'

  static propTypes = {
    options: T.oneOfType([
      T.arrayOf(T.string),
      T.arrayOf(
        T.shape({
          value: T.any,
          display: T.string,
          className: T.string //applied to the dropdown item, as well as the input when this value is selected
        })
      )
    ]),
    saveOnSelect: T.bool,
    scrollable: T.bool,
    menuClasses: T.string,
    requireMatch: T.bool,
    setMenu: T.func,
    ...TextInput.propTypes
  }


  static defaultProps = {
    saveOnSelect: true,
    menuClasses: '',
    requireMatch: false
  }

  constructor(props) {
    super(props)
    this.state = {
      isMenuOpen: false
    }
  }

  focus = () => {
    this._textInputRef.focus()
  }

  openMenu = () => {
    this.setState({ isMenuOpen: true })
  }

  closeMenu = () => {
    this.setState({ isMenuOpen: false })
  }

  getInputRef = () => {
    return this._textInputRef
  }

  _onFocus = () => {
    if (this.props.readOnly) {
      return
    }
    this.openMenu()
    const onFocus = this.props.onFocus
    if (onFocus) {
      onFocus.call(this)
    }
  }

  _onBlur = () => {
    if (!this._ignoreNextBlur) {
      this.closeMenu()
      if (this.props.cancelOnBlur) {
        this._textInputRef.cancel()
      }
    }
    delete this._ignoreNextBlur
    const onBlur = this.props.onBlur
    if (onBlur) {
      onBlur.call(this)
    }
  }

  _onKeyUp = e => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      this.openMenu()
    }
    const onKeyUp = this.props.onKeyUp
    if (onKeyUp) {
      onKeyUp.call(this, e)
    }
  }

  _onSave = (value, ...rest) => {
    // If any of the options match this display value, use the logical value instead
    var option = this._findOptionByDisplay(value)
    if (option) {
      value = _.isObject(option) ? option.value : option
    } else if (this.props.requireMatch) {
      this._textInputRef.cancel()
      return
    }
    this.props.onSave(value, ...rest)
  }

  _onMenuItemClicked = value => {
    if (_.isObject(value)) {
      value = value.display
    }
    this._textInputRef.setValue(value, () => {
      if (this.props.saveOnSelect) {
        this._textInputRef.save()
      }
    })
    this.closeMenu()
  }

  _onMenuMouseDown = () => {
    this._ignoreNextBlur = true
  }

  _findOptionByValue = value => {
    return _.find(
      this.props.options,
      opt => (_.isObject(opt) ? opt.value === value : opt === value)
    )
  }

  _findOptionByDisplay = value => {
    return _.find(
      this.props.options,
      opt => (_.isObject(opt) ? opt.display === value : opt === value)
    )
  }

  _getInputClassName = value => {
    let cls = this.props.className || ''
    const option = this._findOptionByDisplay(value)
    if (_.isObject(option)) {
      cls += ' ' + option.className
    }
    return cls
  }

  _bindInputRef = el => {
    this._textInputRef = el
  }

  _renderMenu = () => {
    const { props } = this

    if (!props.options || !props.options.length) {
      return null
    }

    const menuItems = props.options.map((value, i) => {
      var display, className
      if (_.isObject(value)) {
        display = value.display
        className = value.className
      } else {
        display = value
      }
      return (
        <MenuItem
          key={i}
          onClick={this._onMenuItemClicked}
          args={value}
          className={className}
        >
          {display}
        </MenuItem>
      )
    })

    let menu = (
      <ul>
        {menuItems}
      </ul>
    )
    if (props.scrollable) {
      menu = (
        <ScrollBars classes="pw_scrollbars_light">
          {menu}
        </ScrollBars>
      )
    }

    const menuClasses = cx('dropdown-menu combobox-menu', {
      scrollable: props.scrollable,
      filterable: props.filterable
    })

    return (
      <div
        className={menuClasses + ' ' + props.menuClasses}
        onMouseDown={this._onMenuMouseDown}
      >
        <div className="items">
          {menu}
        </div>
      </div>
    )
  }

  render() {
    const { isMenuOpen } = this.state
    const _props = _.assign({}, this.props, {
      onFocus: this._onFocus,
      onBlur: this._onBlur,
      onKeyUp: this._onKeyUp,
      onSave: this._onSave,
      cancelOnBlur: false, //we handle this ourselves
      className: this._getInputClassName()
    })
    const option = this._findOptionByValue(_props.initialValue)
    if (option) {
      if (_.isObject(option)) {
        _props.initialValue = option.display
      }
    } else if (_props.requireMatch) {
      _props.initialValue = null
    }
    return (
      <MenuOwner
        onClose={this.closeMenu}
        renderMenu={this._renderMenu}
        isMenuOpen={isMenuOpen}
      >
        <TextInput {..._props} ref={this._bindInputRef} />
      </MenuOwner>
    )
  }
}
